import React, { useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";
import { Common } from "@src/styles/Common";
import { MixedChart } from "@src/components";
import ChartHeader, { changeDateType } from "./ChartHeader";
import {
    initTime,
    ParametersType,
    useHomeUserChart,
    SalesStatType,
} from "@src/hooks/queries/useHomeChart";
import { dateFormat, noYearFormat } from "@src/utils/dateUtil";
import { add } from "date-fns";
import moment from "moment";
import "moment/locale/ko";
moment.locale("ko");

function UserStatsChart() {
    const [filterUserChart, setFilterUerChart] = useState<SalesStatType>({
        cycle: "daily",
        startDate: dateFormat(initTime.day.startDate),
        endDate: dateFormat(initTime.day.endDate),
    });

    const parseHomeUserChartAndCreateOption = (data: any, cycle: string) => {
        return {
            labels: data.map((v: any) =>
                cycle === "weekly"
                    ? `${noYearFormat(new Date(v.date))} ~ ${noYearFormat(
                          add(new Date(v.date), {
                              days: 6,
                          }),
                      )}`
                    : v.date,
            ),
            datasets: [
                {
                    type: "line",
                    label: "누적 방문자수",
                    yAxisID: "right",
                    data: data.map((v: any) => v.visitUserAcmlt),
                    borderColor: "#FF007A",
                    backgroundColor: "#FF007A",
                    borderWidth: 3,
                    pointRadius: 2,
                    pointHoverBackgroundColor: Common.colors.white,
                    pointHoverBorderWidth: 3,
                    pointHoverRadius: 5,
                },
                {
                    type: "bar",
                    label: "방문자수",
                    yAxisID: "left",
                    data: data.map((v: any) => v.visitUser),
                    borderColor: Common.colors.orange,
                    backgroundColor: Common.colors.orange,
                    pointRadius: 2,
                    pointHoverBackgroundColor: Common.colors.white,
                    pointHoverBorderWidth: 3,
                    pointHoverRadius: 5,
                },
            ],
        };
    };

    const {
        isLoading: userStatLoading,
        data: userStatData,
        isError: userStatError,
    } = useHomeUserChart(filterUserChart, parseHomeUserChartAndCreateOption);

    const [userVisitor, setUserVisitor] = useState<any>([]);

    useEffect(() => {
        if (!userStatLoading && !userStatError) {
            setUserVisitor(userStatData);
        }
    }, [userStatData]);

    const getDateStatsUserChart = ({ range, type }: changeDateType) => {
        const { startDate, endDate } = range;

        setFilterUerChart({
            cycle: type,
            startDate: dateFormat(startDate),
            endDate: dateFormat(endDate),
        });
    };

    const [options, setOptions] = useState({});
    useEffect(() => {
        switch (filterUserChart.cycle) {
            case "daily": {
                setOptions({
                    scales: {
                        x: {
                            type: "time",
                            time: {
                                unit: "day",
                                displayFormats: {
                                    day: "MM.dd",
                                },
                                tooltipFormat: "YYY.MM.dd",
                            },
                        },
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                        tooltip: {
                            callbacks: {
                                title: (context: any) => {
                                    const label = context[0].label;
                                    const date = moment(label);
                                    return `${date.format("YYYY.MM.DD (dd)")}`;
                                },
                            },
                        },
                    },
                });
                break;
            }
            case "weekly": {
                setOptions({
                    scales: {
                        x: {
                            time: {
                                tooltipFormat: "yyyy.MM.dd",
                            },
                        },
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                    },
                });
                break;
            }
            case "monthly": {
                setOptions({
                    scales: {
                        x: {
                            type: "time",
                            time: {
                                unit: "month",
                                displayFormats: {
                                    month: "yyyy.MM",
                                },
                                tooltipFormat: "YYY.MM.dd",
                            },
                        },
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                    },
                });
                break;
            }
            default: {
                setOptions({
                    scales: {
                        x: {
                            type: "time",
                            time: {
                                displayFormats: { hour: "HH:mm" },
                                tooltipFormat: "YYY.MM.dd HH:mm",
                            },
                            ticks: {
                                autoSkip: true,
                                maxTicksLimit: 12,
                            },
                        },
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                    },
                });
                break;
            }
        }
    }, [filterUserChart]);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    return (
        <div css={subChartDiv}>
            <p css={titleP}>방문자</p>
            <div className="mainChartBox">
                <ChartHeader
                    getChangeDate={getDateStatsUserChart}
                    defaultBasisType="daily"
                />
                <div css={subContainer}>
                    <div css={subTitle}>
                        <LabelName nameColorBg={Common.colors.orange} />
                        <p>방문자 수</p>
                    </div>
                    <div css={subTitle}>
                        <LabelName nameColorBg={"#FF007A"} />
                        <p>누적방문자 수</p>
                    </div>
                </div>
                <div style={{ height: "20px" }}></div>
                {/* <div css={chartBoxStyle} ref={scrollRef}> */}
                <MixedChart
                    data={userVisitor}
                    options={options}
                    onScrollEnd={(val: number) =>
                        scrollRef.current?.scrollTo(val, 0)
                    }
                    boxStyles={chartBoxStyle}
                />
                {/* </div> */}
            </div>
        </div>
    );
}

const subChartDiv = css`
    width: 610px;
    height: 360px;
    padding: 16px;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1.5px solid ${Common.colors.gray200};
`;
const titleP = css`
    ${Common.textStyle.title7_M12}
    color: ${Common.colors.black};
    margin-bottom: 12px;
    font-size: 15px;
`;

const chartBoxStyle = css`
    width: 550px;
    height: 220px;
    overflow: auto;
    ::-webkit-scrollbar {
        border-radius: 4px;
        width: 2px;
        height: 8px;
        margin-right: 10px;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: 20px;
        background-color: #c9c9c9;
    }
    ::-webkit-scrollbar-track {
        border-radius: 20px;
        background-color: transparent;
    }
`;
const subTitle = css`
    display: flex;
    /* width: 50px; */
    justify-content: flex-start;
    align-items: center;
    align-content: center;
    justify-items: center;
    ${Common.textStyle.title7_M12}
`;

const subContainer = css`
    display: flex;
    flex-direction: row;
`;

const LabelName = ({ nameColorBg }: { nameColorBg: string }) => {
    const chartColor = css`
        width: 15px !important;
        height: 15px;
        margin: 8px 5px;
        border-radius: 3px;
        background-color: ${nameColorBg};
    `;
    return <div css={chartColor} />;
};

export default UserStatsChart;
